import * as React from 'react';
import { Check } from 'lucide-react';
import { Select, type SelectOption } from './select';
import { countryToFlag } from '../../utils';

const COUNTRY_CODES = [
  'CH',
  'AF',
  'AL',
  'DZ',
  'AD',
  'AO',
  'AG',
  'AR',
  'AM',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BE',
  'BZ',
  'BJ',
  'BT',
  'BO',
  'BA',
  'BW',
  'BR',
  'BN',
  'BG',
  'BF',
  'BI',
  'CV',
  'KH',
  'CM',
  'CA',
  'CF',
  'TD',
  'CL',
  'CN',
  'CO',
  'KM',
  'CG',
  'CD',
  'CR',
  'CI',
  'HR',
  'CU',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'SZ',
  'ET',
  'FJ',
  'FI',
  'FR',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GR',
  'GD',
  'GT',
  'GN',
  'GW',
  'GY',
  'HT',
  'HN',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IL',
  'IT',
  'JM',
  'JP',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KP',
  'KR',
  'XK',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MR',
  'MU',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NL',
  'NZ',
  'NI',
  'NE',
  'NG',
  'MK',
  'NO',
  'OM',
  'PK',
  'PW',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PL',
  'PT',
  'QA',
  'RO',
  'RU',
  'RW',
  'KN',
  'LC',
  'VC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SK',
  'SI',
  'SB',
  'SO',
  'ZA',
  'SS',
  'ES',
  'LK',
  'SD',
  'SR',
  'SE',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TV',
  'UG',
  'UA',
  'AE',
  'GB',
  'US',
  'UY',
  'UZ',
  'VU',
  'VA',
  'VE',
  'VN',
  'YE',
  'ZM',
  'ZW',
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

const PRIORITY_COUNTRY_CODES: readonly CountryCode[] = [
  'CH',
  'LI',
  'DE',
  'FR',
  'IT',
  'AT',
];

function getOrderedCountryCodes(
  locale = 'en',
  allowedCountries?: readonly CountryCode[],
): CountryCode[] {
  const allowed = allowedCountries
    ? new Set<CountryCode>(allowedCountries)
    : null;
  const availableCodes = allowed
    ? COUNTRY_CODES.filter((code) => allowed.has(code))
    : [...COUNTRY_CODES];
  const available = new Set<CountryCode>(availableCodes);
  const priority = PRIORITY_COUNTRY_CODES.filter((code) => available.has(code));
  const prioritySet = new Set(priority);
  const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
  const collator = new Intl.Collator(locale);
  const rest = availableCodes
    .filter((code) => !prioritySet.has(code))
    .sort((a, b) =>
      collator.compare(displayNames.of(a) ?? a, displayNames.of(b) ?? b),
    );
  return [...priority, ...rest];
}

export interface CountrySelectProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  /** BCP 47 locale tag used to translate country names via Intl.DisplayNames. Defaults to "en". */
  locale?: string;
  /** Placeholder text for the search input. Defaults to "Search..." */
  searchPlaceholder?: string;
  /** Text shown when no countries match the search. Defaults to "No country found." */
  noResultsText?: string;
  /** Countries to display. Defaults to every country in `COUNTRY_CODES`. */
  allowedCountries?: readonly CountryCode[];
  /** Render the menu next to the control instead of in document.body. */
  disablePortal?: boolean;
}

const CountrySelect = React.forwardRef<HTMLButtonElement, CountrySelectProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Select country',
      disabled = false,
      error = false,
      className,
      id,
      locale = 'en',
      searchPlaceholder = 'Search...',
      noResultsText = 'No country found.',
      allowedCountries,
      disablePortal = false,
    },
    ref,
  ) => {
    const displayNames = React.useMemo(
      () => new Intl.DisplayNames([locale], { type: 'region' }),
      [locale],
    );

    const getLabel = React.useCallback(
      (code: string): string => displayNames.of(code) ?? code,
      [displayNames],
    );

    const sortedCodes = React.useMemo(
      () => getOrderedCountryCodes(locale, allowedCountries),
      [allowedCountries, locale],
    );

    const options: SelectOption[] = React.useMemo(
      () => sortedCodes.map((code) => ({ value: code, label: getLabel(code) })),
      [sortedCodes, getLabel],
    );

    return (
      <Select
        ref={ref}
        id={id}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        className={className}
        searchable
        searchPlaceholder={searchPlaceholder}
        noResultsText={noResultsText}
        disablePortal={disablePortal}
        renderOption={(option, selected) => (
          <>
            <span className="text-base leading-none">
              {countryToFlag(option.value)}
            </span>
            <span className="flex-1 truncate">{option.label}</span>
            {selected && <Check className="size-4 text-riso-teal" />}
          </>
        )}
        renderValue={(option) => (
          <span className="flex items-center gap-2">
            <span className="text-base leading-none">
              {countryToFlag(option.value)}
            </span>
            {option.label}
          </span>
        )}
      />
    );
  },
);
CountrySelect.displayName = 'CountrySelect';

export {
  CountrySelect,
  COUNTRY_CODES,
  PRIORITY_COUNTRY_CODES,
  getOrderedCountryCodes,
};
